import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Grid, Chip, Stack, Tabs, Tab, Avatar, Paper, Button, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import StorageIcon from '@mui/icons-material/Storage';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useTechnologies } from '../../hooks/useTechnologies';

const TechnologiesPage = () => {
// eslint-disable-next-line no-unused-vars
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { data: technologiesData = [], isLoading } = useTechnologies({ 
    isPublished: true 
  });

  const techCategories = [
    { label: t('technologies.tabs.all'), icon: <IntegrationInstructionsIcon />, id: 'all' },
    { label: t('technologies.tabs.aiData'), icon: <AutoFixHighIcon />, id: 'ai_ml' },
    { label: t('technologies.tabs.backendCloud'), icon: <StorageIcon />, id: ['backend', 'database', 'cloud'] },
    { label: t('technologies.tabs.frontendMobile'), icon: <DataObjectIcon />, id: ['frontend', 'mobile'] },
    { label: t('technologies.tabs.languages'), icon: <TerminalIcon />, id: 'language' },
    { label: t('technologies.tabs.devopsTools'), icon: <TerminalIcon />, id: ['devops', 'tools', 'integration', 'other'] }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredTechs = activeTab === 0 
    ? technologiesData 
    : technologiesData.filter(tech => {
        const categoryId = techCategories[activeTab].id;
        if (Array.isArray(categoryId)) {
          return categoryId.includes(tech.category);
        }
        return tech.category === categoryId;
      });

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 20 }}>
      {/* Header */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="overline" 
              align="center"
              sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 3, display: 'block', mb: 2 }}
            >
              {t('technologies.header.overline')}
            </Typography>
            <Typography 
              variant="h1" 
              align="center"
              sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              {t('technologies.header.titlePrefix')} <span style={{ color: '#D4AF37' }}>{t('technologies.header.titleHighlight')}</span>
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ color: '#9CA3AF', fontWeight: 300, lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              {t('technologies.header.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {/* Category Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': { bgcolor: '#D4AF37', height: 3 },
              '& .MuiTab-root': { 
                color: '#9CA3AF', 
                fontSize: '1rem', 
                fontWeight: 700,
                minWidth: { xs: '120px', md: '160px' },
                '&.Mui-selected': { color: 'white' }
              }
            }}
          >
            {techCategories.map((cat, idx) => (
              <Tab key={idx} label={cat.label} icon={cat.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#D4AF37' }} />
          </Box>
        ) : (
          /* Tech Grid */
          <Grid container spacing={3}>
            <AnimatePresence mode="popLayout">
              {filteredTechs.map((tech) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={tech.id || tech.name}>
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Paper sx={{ 
                      p: 3, 
                      bgcolor: '#1F2937', 
                      borderRadius: '20px',
                      border: '1px solid rgba(255,255,255,0.05)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#0FA3A3',
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                      }
                    }}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar 
                          src={tech.logo_url}
                          variant="rounded"
                          sx={{ 
                            width: 48,
                            height: 48,
                            bgcolor: 'rgba(255, 255, 255, 0.05)', 
                            p: 1,
                            '& img': { objectFit: 'contain' }
                          }}
                        />
                        <Box>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                            {tech.name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {tech.proficiency && (
                              <Chip 
                                label={tech.proficiency} 
                                size="small" 
                                sx={{ 
                                  height: '18px', 
                                  fontSize: '0.6rem', 
                                  bgcolor: 'rgba(212, 175, 55, 0.1)', 
                                  color: '#D4AF37',
                                  border: '1px solid rgba(212, 175, 55, 0.2)',
                                  textTransform: 'capitalize'
                                }} 
                              />
                            )}
                            {tech.years_experience && (
                              <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.7rem' }}>
                                {tech.years_experience}{" "}
                                {tech.years_experience > 1 ? t("technologies.years.other") : t("technologies.years.one")}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.5, flexGrow: 1 }}>
                        {tech.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Call to Action */}
        <Box 
          sx={{ 
            mt: 12, 
            p: { xs: 4, md: 10 }, 
            bgcolor: 'linear-gradient(135deg, #111827 0%, #0B0F14 100%)', 
            borderRadius: '40px', 
            border: '1px solid rgba(212, 175, 55, 0.1)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: -50, 
              left: -50, 
              width: 200, 
              height: 200, 
              bgcolor: 'rgba(212, 175, 55, 0.05)', 
              borderRadius: '50%', 
              filter: 'blur(60px)' 
            }} 
          />
          <Typography variant="h3" sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontWeight: 700 }}>
            {t('technologies.cta.titlePrefix')} <span style={{ color: '#D4AF37' }}>{t('technologies.cta.titleHighlight')}</span>
          </Typography>
          <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 5, maxWidth: '700px', mx: 'auto', fontWeight: 300 }}>
            {t('technologies.cta.subtitle')}
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => window.location.href='/contact'}
            sx={{ 
              bgcolor: '#D4AF37', 
              color: '#0B0F14', 
              px: 8, 
              py: 2.5, 
              borderRadius: '15px',
              fontWeight: 900,
              fontSize: '1.2rem',
              textTransform: 'none',
              boxShadow: '0 10px 20px rgba(212, 175, 55, 0.2)',
              '&:hover': { bgcolor: '#B8962E', transform: 'scale(1.02)' },
              transition: 'all 0.3s ease'
            }}
          >
            {t('technologies.cta.button')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TechnologiesPage;
